import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router";

import {
    useIsDataRoute,
    useIsProtectedRoute,
    useLocationPotentialRouteState,
    useIsLoginRoute,
    publicRoutes,
    useIsCircleRoute,
} from "@/routing";
import { AuthLayout, MainLayout } from "@/layouts/index";
import useAuthStateListener from "@/auth/useAuthStateListener";
import { setSearchType } from "@/state/searchInputSlice";
import { useIsWebUser } from "@/auth/roles";
import { TranslatedText } from "@/components/common/text";
import { useTheme } from "@/components/layout/useTheme";
import { cn } from "@/utils/css";
import { Toaster } from "@/components/common/notification/Toaster";

export const InitLayout = memo(({ children }) => {
    //TODO Refactor to make single hook with all params
    const isProtectedRoute = useIsProtectedRoute();
    const isDataRoute = useIsDataRoute();
    const isLoginRoute = useIsLoginRoute();
    const isCircleRoute = useIsCircleRoute();
    const routeSearchType = useLocationPotentialRouteState();
    const dispatch = useDispatch();
    const { data: userData } = useSelector((state) => state.profile);
    const searchOpen = useSelector((state) => state.searchInput.searchOpen);
    const searchTopRef = useRef(null);
    const location = useLocation();
    const isWebUser = useIsWebUser();
    const [initialLoad, setInitialLoad] = useState(true);
    const currentYear = new Date().getFullYear();

    useAuthStateListener();
    const { isDarkTheme } = useTheme();

    useLayoutEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth",
        });
    }, [location.pathname]);

    useEffect(() => {
        dispatch(setSearchType(routeSearchType));
    }, [routeSearchType]);

    useEffect(() => {
        if (searchOpen && searchTopRef.current) {
            searchTopRef.current.scrollIntoView({
                behavior: "smooth",
            });
        }
    }, [searchOpen]);

    useEffect(() => {
        if (!!userData && initialLoad) {
            setInitialLoad(false);
        }
    }, [userData]);

    return (
        <div
            className={cn("w-full min-h-screen h-full flex flex-col items-center font-sans justify-start", {
                "transition-colors duration-500 ease-in-out": !initialLoad,
            })}
        >
            {isCircleRoute ? (
                <div className="absolute top-0 left-0 w-full min-h-screen -z-10 overflow-hidden">
                    <div
                        className={cn(
                            "absolute top-0 left-0 w-full h-[100vw] bg-[radial-gradient(50%_50%_at_50%_50%,_var(--tw-gradient-from)_0%,_var(--tw-gradient-to)_100%)] from-cm-accent-light to-cm-container",
                            {
                                "opacity-[0.07]": isDarkTheme,
                                "opacity-[0.1]": !isDarkTheme,
                            },
                        )}
                    />
                </div>
            ) : null}

            {isProtectedRoute ? (
                <AuthLayout>
                    {isWebUser === undefined || !userData ? null : isWebUser ? (
                        <MainLayout searchTopRef={searchTopRef}>{children}</MainLayout>
                    ) : (
                        <TranslatedText>You don't have access to this page</TranslatedText>
                    )}
                </AuthLayout>
            ) : isLoginRoute ? (
                <>{children}</>
            ) : (
                <MainLayout searchTopRef={searchTopRef}>{children}</MainLayout>
            )}
            <div className="flex flex-row gap-4 text-sm/[22px] text-cm-p-text-secondary mb-2 items-end flex-1">
                <div>{`© ${currentYear} CargoMinds`}</div>
                <div>
                    <Link className="text-sm/[22px] text-cm-p-text-secondary" to={publicRoutes[1].path}>
                        Privacy
                    </Link>
                </div>
            </div>
            <Toaster position="bottom-left" duration={10000} />
        </div>
    );
});
